import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import { registerChronicleSessionDeviceFileAtom } from '@/atoms/chronicle/session-device-files';
import { chronicleBridge } from '@/tauri/chronicle';
import { listenChronicleAutoSessionCreated } from '@/tauri/chronicle/events';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import { trackHardwareSessionCreated } from '@/utils/analytics/track-hardware-events';

/**
 * iOS parity: consume `chronicle:auto_session_created` —
 * the auto-session watcher created a genuinely-NEW backend session for a
 * device-initiated live recording. Fires `hardware_session_created`
 * (source: live_recording — duration/size unknown at creation, matching iOS)
 * and registers the sessionId → device-filename pairing for downstream
 * device-file→note tracing.
 *
 * Recovered interrupted recordings never reach this event — the Rust watcher
 * emits `chronicle:recording_recovered` for those instead — so the "not for
 * duplicates / recovered sessions" firing rule holds structurally.
 *
 * Mounted once by ChronicleStateProvider.
 */
export function useChronicleAutoSessionCreatedListener() {
  const registerSessionDeviceFile = useSetAtom(registerChronicleSessionDeviceFileAtom);

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let unlistenFn: (() => void) | null = null;
    // Same teardown-race guard as the recording_recovered listener (APP-7607).
    let cancelled = false;

    const setup = async () => {
      try {
        const unlisten = await listenChronicleAutoSessionCreated((event) => {
          const { sessionId, fileName } = event.payload;
          // Log identifiers only — the device fileName is an identifier the
          // Rust path sanitizer does not cover, same as the sibling
          // recording_recovered listener.
          logger.info({
            msg: 'Chronicle auto_session_created event received',
            sessionId,
            hasDeviceFile: Boolean(fileName),
          });
          trackHardwareSessionCreated({
            sessionSource: 'live_recording',
            deviceFileName: fileName ?? undefined,
            sessionId,
          });
          if (fileName) {
            registerSessionDeviceFile({
              sessionId,
              deviceFileName: fileName,
              sourceType: SESSION_SOURCE_TYPE.ChronicleLiveRecording,
            });
          }
        });

        if (cancelled) {
          unlisten();
          return;
        }
        unlistenFn = unlisten;
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle auto_session_created listener',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    return () => {
      cancelled = true;
      unlistenFn?.();
    };
  }, [registerSessionDeviceFile]);
}
